/* eslint-disable max-len */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { FlashPromotionPublicationTableData } from 'pages/BackOfficePage/BackOfficeCategories/Promotion/CreatePromotion/PublishStep/PublishStep.types';
import { STable } from 'pages/EstimatePage/EstimatePage.styled';
import { GetTitle } from 'pages/EstimatePage/TableSection/tableComponents';
import { Flex, Text, WithTooltip } from 'UI';

export const TableTimeLimited = ({ data }: { data: FlashPromotionPublicationTableData }) => {
  const { t } = useTranslation();
  const getDataColumns = () => {
    return [
      {
        title: GetTitle(t('backoffice.promotion.publication_preview.promotion_title', 'Promotion title')),
        dataIndex: 'title',
        key: 'title',
        render: function renderTitle(title: string) {
          return (
            <Flex maxWidth={150} justify={'center'} align={'center'} data-cy={'promotion-title-value'}>
              <WithTooltip title={title}>
                <Text type={'text'} ellipsis data-cy={'promotion-title'}>
                  {title}
                </Text>
              </WithTooltip>
            </Flex>
          );
        },
      },
      {
        title: GetTitle(t('backoffice.promotion.price_option', 'Price option')),
        dataIndex: 'discountType',
        key: 'discountType',
        render: function renderDiscountType(discountType: string) {
          const discountTypeText = t(
            'backoffice.promotion.publication_preview.discount_type',
            'Based on {{discountType}}',
            { discountType },
          );
          return (
            <Flex minWidth={120} justify={'center'} align={'center'} data-cy={'price-option-value'}>
              <WithTooltip title={discountTypeText}>
                <Text type={'text'} data-cy={'price-option'}>
                  {discountTypeText}
                </Text>
              </WithTooltip>
            </Flex>
          );
        },
      },
      {
        title: GetTitle(t('backoffice.promotion.publication_preview.period', 'Period')),
        dataIndex: 'period',
        key: 'period',
        width: 75,
        render: function renderPeriod(period: { publicationStart: string; publicationEnd: string }) {
          const periodTooltipText = `${period.publicationStart} - ${period.publicationEnd}`;
          return (
            <Flex minWidth={90} direction={'column'} justify={'center'} align={'center'} data-cy={'period-value'}>
              <WithTooltip title={periodTooltipText}>
                <Text type={'text'} data-cy={'publication-start-value'}>
                  {period.publicationStart}
                </Text>
              </WithTooltip>
              <WithTooltip title={periodTooltipText}>
                <Text type={'text'} data-cy={'publication-end-value'}>
                  {period.publicationEnd}
                </Text>
              </WithTooltip>
            </Flex>
          );
        },
      },
      {
        title: GetTitle(t('backoffice.promotion.publication_preview.commercial_family', 'Commercial family')),
        dataIndex: 'commercialFamilyCodes',
        key: 'commercialFamilyCodes',
        render: function renderCommercialFamily(commercialFamilyCodes: string) {
          return (
            <Flex maxWidth={150} justify={'center'} align={'center'} data-cy={'commercial-family-value'}>
              <WithTooltip title={commercialFamilyCodes}>
                <Text type={'text'} ellipsis={true} data-cy={'commercial-family'}>
                  {commercialFamilyCodes}
                </Text>
              </WithTooltip>
            </Flex>
          );
        },
      },
      {
        title: GetTitle(t('backoffice.promotion.publication_preview.references', 'References')),
        dataIndex: 'referencesTimeLimitedFileName',
        key: 'referencesTimeLimitedFileName',
        render: function renderReferences(references: string) {
          return (
            <Flex maxWidth={150} justify={'center'} align={'center'} data-cy={'references-value'}>
              <WithTooltip title={references}>
                <Text type={'text'} data-cy={'references-list'}>
                  {references}
                </Text>
              </WithTooltip>
            </Flex>
          );
        },
      },
      {
        title: GetTitle(t('backoffice.promotion.publication_preview.discount', 'Discount')),
        dataIndex: 'discount',
        key: 'discount',
        render: function renderDiscount(discount: string) {
          const discountText = `${discount}%`;
          return (
            <Flex justify={'center'} align={'center'} data-cy={'discount-value'}>
              <WithTooltip title={discountText}>
                <Text type={'text'} data-cy={'discount'}>
                  {discountText}
                </Text>
              </WithTooltip>
            </Flex>
          );
        },
      },
      {
        title: GetTitle(t('backoffice.promotion.target', 'Target')),
        dataIndex: 'targetedAudience',
        key: 'targetedAudience',
        render: function renderTarget(targetedAudience: { dealerTypes: string; fileName: string }) {
          const targetedAudienceToolTipTitle = `${targetedAudience.dealerTypes} ${targetedAudience.fileName}`;
          return (
            <Flex direction={'column'} justify={'center'} align={'center'} data-cy={'target-container'}>
              <WithTooltip title={targetedAudienceToolTipTitle}>
                <Text type={'text'} data-cy={'target-dealer-types'}>
                  {targetedAudience.dealerTypes}
                </Text>
              </WithTooltip>
              <WithTooltip title={targetedAudienceToolTipTitle}>
                <Text type={'text'} data-cy={'target-audiences-types'}>
                  {targetedAudience.fileName}
                </Text>
              </WithTooltip>
            </Flex>
          );
        },
      },
    ];
  };
  if (!data) {
    return null;
  }
  return (
    <STable
      key={'promotions_flash_time_limited_table'}
      dataSource={[data]}
      columns={getDataColumns()}
      pagination={false}
      data-cy={'promotion-details-table'}
    />
  );
};
